// v1.22.0 · Auditoría interna: contenedor del módulo (permisos, empresas, pestañas). Tema neumórfico.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Building2, LogOut, Plus, RefreshCw, Settings2, ShieldCheck } from 'lucide-react';
import './neu.css';
import { C, api, errMsg, fdate, waitJob } from './api';
import Panel from './Panel';
import Expediente from './Expediente';
import Conciliaciones from './Conciliaciones';
import Banderas from './Banderas';
import Plan from './Plan';
import Informes from './Informes';

const ROLES = ['admin', 'gerencia', 'contratos', 'auditoria'];
export const canUseAudit = (u) => !!u && (ROLES.includes(u.role) || u.username === 'Gerencia' || (u.extra_modules || []).includes('auditoria'));
const HOME = { contratos: '/contratos', customer: '/customer', mercado: '/mercado', mercado_consulta: '/mercado', qa: '/qa', client_qa: '/qa' };
const TABS = [['panel', 'Panel'], ['banderas', 'Banderas rojas'], ['conciliaciones', 'Conciliaciones'], ['expediente', 'Expediente'],
  ['plan', 'Plan de cuentas'], ['informes', 'Informes IA']];
const EMPTY = { name: '', ruc: '', sector: '', nonprofit: false, donation_min_pct: 5, materiality_pct: 0.5, materiality_amount: 0, audit_firm: '', auditor_name: '', notes: '', sign_mode: 'auto', pyme_max_activos: 4000000, pyme_max_ventas: 5000000, pyme_max_trabajadores: 200 };

export default function AuditoriaPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [filters, setFilters] = useState({});
  const [openKey, setOpenKey] = useState(null);
  const cid = params.get('empresa') || '';
  const tab = TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'panel';

  useEffect(() => {
    let u = null;
    try { u = JSON.parse(localStorage.getItem('user') || 'null'); } catch { u = null; }
    if (!u) { navigate('/login'); return; }
    if (!canUseAudit(u)) { toast.error('No tiene acceso a Auditoría interna', { id: 'aud-denied' }); navigate(HOME[u.role] || '/finanzas'); return; }
    setUser(u);
  }, [navigate]);

  const loadCompanies = useCallback(async () => {
    try {
      const cs = await api.get('/companies');
      setCompanies(cs);
      if (!cid && cs.length) setParams({ empresa: cs[0].id, tab: 'panel' }, { replace: true });
    } catch (e) { toast.error(errMsg(e)); setCompanies([]); }
  }, [cid, setParams]);
  useEffect(() => { if (user) loadCompanies(); }, [user]);   // eslint-disable-line react-hooks/exhaustive-deps

  const loadAnalysis = useCallback(async () => {
    if (!cid) { setAnalysis(null); return; }
    setLoading(true);
    try { setAnalysis(await api.get(`/companies/${cid}/analysis`)); } catch (e) { toast.error(errMsg(e)); setAnalysis(null); }
    setLoading(false);
  }, [cid]);
  useEffect(() => { if (user) loadAnalysis(); }, [user, loadAnalysis]);

  const go = (k, f) => { if (f) setFilters(f); setParams({ empresa: cid, tab: k }); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const openFlag = (key) => { setOpenKey(key); setParams({ empresa: cid, tab: 'banderas' }); };
  const company = (companies || []).find((c) => c.id === cid);
  const refresh = async () => {
    setLoading(true);
    try { await api.post(`/companies/${cid}/analyze`); await loadAnalysis(); toast.success('Análisis recalculado'); } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  const saveCompany = async () => {
    try {
      const body = { ...form, donation_min_pct: +form.donation_min_pct, materiality_pct: +form.materiality_pct, materiality_amount: +form.materiality_amount,
        pyme_max_activos: +form.pyme_max_activos || 4000000, pyme_max_ventas: +form.pyme_max_ventas || 5000000, pyme_max_trabajadores: +form.pyme_max_trabajadores || 200 };
      if (form.id) {
        const { id, created_at: _c, created_by: _b, score: _s, corte: _co, analyzed_at: _a, files: _f, ...rest } = body;
        const r = await api.put(`/companies/${id}`, rest);
        if (r.job_id) {   // cambió el signo: se releen estados y mayores
          setForm(null);
          toast('Releyendo estados financieros y mayores con el nuevo signo…');
          const j = await waitJob(r.job_id);
          if (j.status === 'done') toast.success(`Listo: ${j.result?.releidos ?? 0} archivo(s) releído(s)`); else toast.error(j.message);
        } else toast.success('Empresa actualizada');
        loadAnalysis();
      } else {
        const c = await api.post('/companies', body);
        toast.success('Empresa creada. Suba ahora su plan de cuentas y estados.');
        setParams({ empresa: c.id, tab: 'expediente' });
      }
      setForm(null);
      loadCompanies();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const deleteCompany = async () => {
    const name = window.prompt(`Para borrar la empresa y TODO su expediente escriba su nombre exacto:\n${company.name}`);
    if (!name) return;
    try { await api.del(`/companies/${cid}`, { confirm: name }); toast.success('Empresa borrada'); setForm(null); setParams({}); loadCompanies(); } catch (e) { toast.error(errMsg(e)); }
  };
  const nAlta = analysis?.score?.open?.alta || 0;

  return (
    <div className="neu" data-testid="auditoria-page">
      <header style={{ position: 'sticky', top: 0, zIndex: 20, background: 'rgba(233,237,243,.92)', backdropFilter: 'blur(8px)' }}>
        <div style={{ maxWidth: 1440, margin: '0 auto', padding: '12px 16px' }} className="neu-row">
          <button type="button" className="neu-btn neu-btn-sm" onClick={() => navigate(user ? (HOME[user.role] || '/finanzas') : '/')} aria-label="Volver a módulos"><ArrowLeft size={15} /><span className="hidden sm:inline">Módulos</span></button>
          <div className="neu-row" style={{ gap: 8 }}>
            <div className="neu-card" style={{ padding: 8, borderRadius: 14 }}><ShieldCheck size={20} color={C.brand} /></div>
            <div><p className="neu-h1" style={{ fontSize: 17 }}>Auditoría interna</p><p className="neu-sub" style={{ fontSize: 11 }}>Expediente · banderas rojas · informes con IA</p></div>
          </div>
          <span className="neu-spacer" />
          <div className="neu-row" style={{ gap: 8 }}>
            <Building2 size={16} color={C.ink2} aria-hidden="true" />
            <select className="neu-select" style={{ width: 'min(320px, 60vw)' }} value={cid} aria-label="Empresa"
                    onChange={(e) => { setOpenKey(null); setFilters({}); setParams({ empresa: e.target.value, tab }); }}>
              {!companies?.length && <option value="">Sin empresas</option>}
              {(companies || []).map((c) => <option key={c.id} value={c.id}>{c.name}{c.ruc ? ` · ${c.ruc}` : ''}</option>)}
            </select>
            {company && <button type="button" className="neu-btn neu-icon-btn" title="Datos de la empresa" aria-label="Datos de la empresa" onClick={() => setForm({ ...EMPTY, ...company })}><Settings2 size={15} /></button>}
            <button type="button" className="neu-btn neu-btn-sm neu-btn-primary" onClick={() => setForm({ ...EMPTY })}><Plus size={14} /> Empresa</button>
            {cid && <button type="button" className="neu-btn neu-icon-btn" title="Recalcular análisis" aria-label="Recalcular análisis" onClick={refresh} disabled={loading}><RefreshCw size={15} className={loading ? 'animate-spin' : ''} /></button>}
            <button type="button" className="neu-btn neu-icon-btn" title="Salir" aria-label="Salir" onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/login'); }}><LogOut size={15} /></button>
          </div>
        </div>
        {cid && (
          <nav style={{ maxWidth: 1440, margin: '0 auto', padding: '0 12px' }} className="neu-tabs" aria-label="Secciones">
            {TABS.map(([k, l]) => (
              <button type="button" key={k} className={`neu-tab ${tab === k ? 'is-on' : ''}`} onClick={() => go(k)} aria-current={tab === k ? 'page' : undefined}>
                {l}{k === 'banderas' && nAlta > 0 && <span className="neu-badge" aria-label={`${nAlta} altas`}>{nAlta}</span>}
              </button>
            ))}
          </nav>
        )}
      </header>

      <main style={{ maxWidth: 1440, margin: '0 auto', padding: '12px 16px 60px' }}>
        {companies && !companies.length && (
          <div className="neu-card neu-empty" style={{ padding: 48 }}>
            <ShieldCheck size={34} color={C.brand} />
            <p className="neu-h1" style={{ marginTop: 12 }}>Cree la primera empresa a auditar</p>
            <p className="neu-hint" style={{ maxWidth: 560, margin: '8px auto 16px' }}>Cada empresa tiene su propio plan de cuentas, su expediente mensual acumulado, sus banderas rojas y sus informes.</p>
            <button type="button" className="neu-btn neu-btn-primary" onClick={() => setForm({ ...EMPTY })}><Plus size={14} /> Nueva empresa</button>
          </div>
        )}
        {cid && company && analysis?.updated_at && <p className="neu-hint" style={{ margin: '4px 4px 14px' }}>{company.name} · análisis del {fdate(analysis.updated_at)} · {analysis.periods?.length || 0} mes(es) de mayor · corte {fdate(analysis.corte)}</p>}
        {cid && !analysis && loading && <div className="neu-card neu-empty">Calculando el análisis…</div>}
        {cid && analysis && (
          <>
            {tab === 'panel' && <Panel cid={cid} analysis={analysis} onOpenFlag={openFlag} goTab={go} />}
            {tab === 'banderas' && <Banderas cid={cid} analysis={analysis} filters={filters} setFilters={setFilters} openKey={openKey} setOpenKey={setOpenKey} onChanged={loadAnalysis} />}
            {tab === 'conciliaciones' && <Conciliaciones analysis={analysis} goTab={go} />}
            {tab === 'expediente' && <Expediente cid={cid} analysis={analysis} onChanged={() => { loadAnalysis(); loadCompanies(); }} />}
            {tab === 'plan' && <Plan cid={cid} onOpenFlag={openFlag} onChanged={loadAnalysis} goTab={go} />}
            {tab === 'informes' && <Informes cid={cid} analysis={analysis} onChanged={loadCompanies} />}
          </>
        )}
      </main>

      {form && (
        <div className="neu-modal-bg" role="dialog" aria-modal="true" aria-label="Empresa" onClick={(e) => e.target === e.currentTarget && setForm(null)}>
          <div className="neu-modal">
            <p className="neu-title" style={{ fontSize: 17 }}>{form.id ? 'Datos de la empresa' : 'Nueva empresa'}</p>
            <div className="neu-grid neu-grid-2" style={{ gap: 12, marginTop: 14 }}>
              <label className="neu-field" style={{ gridColumn: '1 / -1' }}><span className="neu-label">Razón social</span>
                <input className="neu-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus /></label>
              <label className="neu-field"><span className="neu-label">RUC</span><input className="neu-input" value={form.ruc} onChange={(e) => setForm({ ...form, ruc: e.target.value })} /></label>
              <label className="neu-field"><span className="neu-label">Sector</span><input className="neu-input" placeholder="Educación, comercio…" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} /></label>
              <label className="neu-field"><span className="neu-label">Materialidad (% del activo)</span>
                <input className="neu-input" type="number" step="0.1" min="0.01" max="10" value={form.materiality_pct} onChange={(e) => setForm({ ...form, materiality_pct: e.target.value })} /></label>
              <label className="neu-field"><span className="neu-label">o monto fijo (USD)</span>
                <input className="neu-input" type="number" min="0" value={form.materiality_amount} onChange={(e) => setForm({ ...form, materiality_amount: e.target.value })} /></label>
              <label className="neu-field" style={{ gridColumn: '1 / -1' }}><span className="neu-label">Signo de pasivo, patrimonio e ingresos en los reportes del sistema contable</span>
                <select className="neu-select" value={form.sign_mode || 'auto'} onChange={(e) => setForm({ ...form, sign_mode: e.target.value })}>
                  <option value="auto">Detectar en cada archivo (recomendado)</option>
                  <option value="natural">Vienen en positivo (un negativo es saldo contrario)</option>
                  <option value="negativo">Vienen en negativo (un negativo es su saldo normal)</option>
                </select>
                <span className="neu-hint">Algunos sistemas exportan las cuentas acreedoras con signo negativo. El sistema las convierte a positivo para los
                  indicadores, gráficos y banderas. Si lo cambia, se vuelven a leer los estados y mayores ya cargados.</span></label>
              <label className="neu-row" style={{ fontSize: 13, gridColumn: '1 / -1' }}>
                <input type="checkbox" checked={form.nonprofit} onChange={(e) => setForm({ ...form, nonprofit: e.target.checked })} /> Sin fines de lucro (revisar mínimo de donaciones para la exención)</label>
              {form.nonprofit && <label className="neu-field"><span className="neu-label">Mínimo de donaciones (% de ingresos)</span>
                <input className="neu-input" type="number" min="0" max="100" value={form.donation_min_pct} onChange={(e) => setForm({ ...form, donation_min_pct: e.target.value })} /></label>}
              <details style={{ gridColumn: '1 / -1' }}>
                <summary className="neu-hint" style={{ cursor: 'pointer' }}>Marco contable: NIIF para las PYMES · límites de calificación de la SCVS</summary>
                <div className="neu-grid neu-grid-3" style={{ gap: 10, marginTop: 8 }}>
                  <label className="neu-field"><span className="neu-label">Activos menores a (USD)</span>
                    <input className="neu-input" type="number" min="1" value={form.pyme_max_activos} onChange={(e) => setForm({ ...form, pyme_max_activos: e.target.value })} /></label>
                  <label className="neu-field"><span className="neu-label">Ventas brutas anuales hasta (USD)</span>
                    <input className="neu-input" type="number" min="1" value={form.pyme_max_ventas} onChange={(e) => setForm({ ...form, pyme_max_ventas: e.target.value })} /></label>
                  <label className="neu-field"><span className="neu-label">Trabajadores hasta</span>
                    <input className="neu-input" type="number" min="1" value={form.pyme_max_trabajadores} onChange={(e) => setForm({ ...form, pyme_max_trabajadores: e.target.value })} /></label>
                </div>
                <span className="neu-hint">Resolución SC.INPA.UA.G-10.005. Cámbielos solo si la Superintendencia publica montos nuevos.</span>
              </details>
              <label className="neu-field"><span className="neu-label">Firma auditora</span><input className="neu-input" value={form.audit_firm} onChange={(e) => setForm({ ...form, audit_firm: e.target.value })} /></label>
              <label className="neu-field"><span className="neu-label">Responsable que firma</span><input className="neu-input" value={form.auditor_name} onChange={(e) => setForm({ ...form, auditor_name: e.target.value })} /></label>
              <label className="neu-field" style={{ gridColumn: '1 / -1' }}><span className="neu-label">Notas</span>
                <textarea className="neu-textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
            </div>
            <div className="neu-row" style={{ marginTop: 18 }}>
              {form.id && <button type="button" className="neu-btn neu-btn-danger" onClick={deleteCompany}>Borrar empresa</button>}
              <span className="neu-spacer" />
              <button type="button" className="neu-btn" onClick={() => setForm(null)}>Cancelar</button>
              <button type="button" className="neu-btn neu-btn-primary" onClick={saveCompany} disabled={(form.name || '').trim().length < 2}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
